import { useCallback, useState } from "react";
import { useRevealDownload } from "@/hooks/useRevealDownload";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { convertStlBatch, readStlItem, targetIsRedundant, type StlItem, type StlTarget } from "./stlBatch";

const TOOL_INFO_CONTENT = {
  what: `Converts an STL file between its ASCII (human-readable text) and binary (compact) representations, auto-detecting which format your file is currently in.`,
  how: `Upload your STL file (or several at once), and convert it to either ASCII or binary format. A file that is not a real STL is flagged straight away and can't be converted. One file downloads as an .stl; several download as one ZIP.`,
  tip: `This converts the geometry only: the 80-byte header text and any per-triangle colour bytes are not carried over, and the face normals are recalculated from the triangles. Binary STL files are significantly smaller than the equivalent ASCII version for the same geometry — if file size matters for storage or transfer, binary is almost always the better choice.`,
};

export default function StlAsciiBinaryConverter() {
  const [items, setItems] = useState<StlItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof convertStlBatch>> | null>(null);

  const reveal = useRevealDownload(!!result);
  const validCount = items.filter((i) => i.check.ok).length;
  const single = items.length === 1 ? items[0] : null;

  // Files are read and checked as soon as they are chosen, so a bad file is reported at once.
  // Nothing is charged here: a trial use is only counted when the converted file is downloaded.
  const handleFiles = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    setResult(null);
    setProcessing(true);
    try { setItems(await Promise.all(files.map(readStlItem))); } finally { setProcessing(false); }
  }, []);

  const convert = async (to: StlTarget) => {
    if (validCount === 0) return;
    setProcessing(true);
    try {
      const r = await convertStlBatch(items, to);
      setResult(r);
      toast.success(r.single ? `Converted to ${to} STL` : `Converted ${r.files.length} files to ${to} STL`);
    } catch {
      toast.error("Couldn't convert this STL file");
    } finally {
      setProcessing(false);
    }
  };

  const noneValid = items.length > 0 && validCount === 0;
  const tip = (to: StlTarget) => (noneValid ? "No valid STL file to convert" : targetIsRedundant(items, to) ? `${items.length > 1 ? "These files are" : "This file is"} already ${to === "ascii" ? "ASCII" : "Binary"} STL` : undefined);

  return (
    <ToolContainer>
      {items.length === 0 ? (
        <FileDropZone gateAtDownload
          accept=".stl"
          multiple
          onFiles={handleFiles}
          label="Drop an STL file"
          description="Convert between ASCII and binary STL, auto-detects the current format. Drop several files to get one ZIP."
        />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">{single ? <span className="break-all">{single.name}</span> : `${items.length} files (${validCount} valid)`}</p>
              <Button size="sm" variant="ghost" onClick={() => { setItems([]); setResult(null); }}>
                Change file{items.length > 1 ? "s" : ""}
              </Button>
            </div>
            {single ? (
              single.check.ok
                ? <p className="text-xs text-muted-foreground">Detected as {single.check.format.toUpperCase()} STL, {single.check.triangleCount.toLocaleString()} triangles</p>
                : <p role="alert" className="text-sm text-destructive">{single.check.reason}</p>
            ) : (
              <ul className="text-xs space-y-1">
                {items.map((i, n) => (
                  <li key={n} className={i.check.ok ? "text-muted-foreground" : "text-destructive"}>
                    <span className="break-all font-medium">{i.name}</span>: {i.check.ok ? `${i.check.format.toUpperCase()} STL, ${i.check.triangleCount.toLocaleString()} triangles` : i.check.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {processing ? (
            <ProcessingState />
          ) : (
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => convert("ascii")} disabled={noneValid || targetIsRedundant(items, "ascii")} title={tip("ascii")}>
                Convert to ASCII STL
              </Button>
              <Button variant="outline" onClick={() => convert("binary")} disabled={noneValid || targetIsRedundant(items, "binary")} title={tip("binary")}>
                Convert to Binary STL
              </Button>
            </div>
          )}
          {noneValid && !processing && <p className="text-xs text-muted-foreground">Choose a different file: only real STL files can be converted.</p>}

          {result && (
            <Card className="p-4 space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-lg font-bold text-primary">
                    {(result.single ? result.stats.vertexCount : result.files.length).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">{result.single ? "Vertices" : "Files"}</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-primary">
                    {(result.single ? result.stats.triangleCount : result.files.reduce((n, f) => n + f.triangles, 0)).toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Triangles</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-primary">
                    {(result.blob.size / 1024).toFixed(1)} KB
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {result.single ? `${result.format} size` : "ZIP size"}
                  </p>
                </div>
              </div>
              {result.skipped.length > 0 && <p className="text-xs font-medium">Left out because they are not valid STL files: {result.skipped.join(", ")}</p>}
              {result.files.some((f) => f.same) && !result.single && <p className="text-xs text-muted-foreground">Files already in {result.format} format were included unchanged.</p>}
              <div ref={reveal.ref} className={reveal.className}><DownloadButton blob={result.blob} fileName={result.name} /></div>
            </Card>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
